import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { SavedJobsProvider } from './context/SavedJobsContext';
import { VideoFavoritesProvider } from './context/VideoFavoritesContext';
import { DraftProvider } from './context/DraftContext';
import { CartProvider } from './context/CartContext';
import { FullPageSkeleton } from './components/Skeletons/Skeleton';
import { Toaster } from 'react-hot-toast';
import './App.css';
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

const Home = lazy(() => import('./components/Home'));
const JobSeekerAuth = lazy(() => import("./components/JobSeekerAuth"));
const EmployeeAuth = lazy(() => import("./components/EmployeeAuth"));
const ModalRegister = lazy(() => import('./components/ModalRegister'));
const Profile = lazy(() => import('./components/Profile'));
const JobSearch = lazy(() => import('./pages/JobSearch'));
const JobDetails = lazy(() => import('./pages/JobDetails'));
const JobSeekerHome = lazy(() => import('./pages/JobSeekerHome'));
const SavedJobs = lazy(() => import('./pages/SavedJobs'));
const RecommendedJobs = lazy(() => import('./pages/RecommendedJobs'));
const MyApplications = lazy(() => import('./pages/MyApplications'));
const CompanyApplications = lazy(() => import('./pages/CompanyApplications'));
const JobPosting = lazy(() => import('./components/JobPosting'));
const ContactUs = lazy(() => import('./components/ContactUs'));
const MVProfile = lazy(() => import('./components/MVProfile'));
const CoursePage = lazy(() => import('./components/CoursePage'));
const CoursesListPage = lazy(() => import('./components/CoursesListPage'));
const AudioCourseAdmin = lazy(() => import('./pages/AudioCourseAdmin'));
const CompanyNavbar = lazy(() => import('./components/CompanyNavbar/CompanyNavbar'));
const ExcelUploadComponent = lazy(() => import('./components/CourseManagement'));
const SharedAudioCoursePage = lazy(() => import('./components/SharedAudioCoursePage'));
const SharedVideoCoursePage = lazy(() => import('./components/SharedVideoCoursePage'));
const VideoCoursesView = lazy(() => import('./components/VideoCoursesView'));
const CreatorHubPage = lazy(() => import('./pages/CreatorHubPage'));
const CreatorApplicationPage = lazy(() => import('./pages/CreatorApplicationPage'));
const ArchivedCoursesView = lazy(() => import('./components/ArchivedCoursesView'));
const VideoCourseHomePage = lazy(() => import('./components/VideoCourseHomePage'));
const ManageCoursesPage = lazy(() => import('./components/ManageCoursesPage'));
const SearchResultsPage = lazy(() => import('./components/SearchResultsPage'));
const VideoCourseExcelUpload = lazy(() => import('./components/VideoCourseExcelUpload'));
const VideoCourseVideoUpload = lazy(() => import('./components/VideoCourseVideoUpload'));
const EditCourseOptions = lazy(() => import('./components/EditCourseOptions')); 
const VideoSearchResultsPage = lazy(() => import('./components/VideoSearchResultsPage'));
const VideoCourseDetailsPage = lazy(() => import('./components/VideoCourseDetailsPage'));
const StudentVideoCourseDetailsPage = lazy(() => import('./components/StudentVideoCourseDetailsPage'));
const CommonNavbar = lazy(() => import('./components/CompanyNavbar/CompanyNavbar'));
const CompanySettings = lazy(() => import('./components/CompanySettings/CompanySettings'));
const CompanyDashboard = lazy(() => import('./components/CompanyDashboard/CompanyDashboard'));
const CompanyProfile = lazy(() => import('./components/CompanyProfile/CompanyProfile'));
const CompanyOnboardingGuard = lazy(() => import('./components/CompanyOnboarding/CompanyOnboardingGuard'));

const SubscriptionPlans = lazy(() => import('./components/Subscription/SubscriptionPlans'));
const Checkout = lazy(() => import('./components/Subscription/Checkout'));
const SubscriptionSuccess = lazy(() => import('./components/Subscription/SubscriptionSuccess'));
const MySubscriptions = lazy(() => import('./components/Subscription/MySubscriptions'));
const MyCoursePurchases = lazy(() => import('./components/Subscription/MyCoursePurchases'));
const FloatingUpgradeButton = lazy(() => import('./components/FloatingUpgradeButton'));
const LearningCoursesList = lazy(() => import('./components/courses/LearningCoursesList'));
const VideoLearningPage = lazy(() => import('./components/VideoLearningPage'));
const ManageJobsPage = lazy(() => import('./components/ManageJobs/ManageJobsPage'));
const JobDetailsPage = lazy(() => import('./components/ManageJobs/JobDetailsPage'));
const EditJobPage = lazy(() => import('./components/ManageJobs/EditJobPage'));
const ResdexPage = lazy(() => import('./components/Resdex/ResdexPage'));
const ResdexResultsPage = lazy(() => import('./components/Resdex/ResdexResultsPage'));
const DraftApplicationsPage = lazy(() => import('./components/DraftApplications/DraftApplicationsPage'));
const ArchivedAudioCoursesPage = lazy(() => import('./components/ManageJobs/ArchivedAudioCoursesPage'));
const ArchivedAudioCourseDetailPage = lazy(() => import('./components/ManageJobs/ArchivedAudioCourseDetailPage'));
const ArchivedVideoCoursesPage = lazy(() => import('./components/ManageJobs/ArchivedVideoCoursesPage'));
const ArchivedVideoCourseDetailPage = lazy(() => import('./components/ManageJobs/ArchivedVideoCourseDetailPage'));
const CandidateSearchPage = lazy(() => import('./components/CandidateSearch/CandidateSearchPage'));
const CandidateApplicantsPage = lazy(() => import('./components/CandidateSearch/CandidateApplicantsPage'));
const DraftVideoCoursesView = lazy(() => import('./components/DraftVideoCoursesView'));
const MobileVerification = lazy(() => import('./components/MobileVerification'));
const PhoneLogin = lazy(() => import('./components/PhoneLogin'));
const CoursesLandingPage = lazy(() => import('./pages/CoursesLandingPage'));
const AdminContactDetailsPage    = lazy(() => import('./components/ContactUs/AdminContactUs/AdminContactDetailsPage'));
const AdminMessagesPage          = lazy(() => import('./components/ContactUs/AdminContactUs/AdminMessagesPage'));
const AdminMessageDetailPage     = lazy(() => import('./components/ContactUs/AdminContactUs/AdminMessageDetailPage'));
const AdminFaqsPage              = lazy(() => import('./components/ContactUs/AdminContactUs/AdminFaqsPage'));
const AdminMessageSubjectsPage   = lazy(() => import('./components/ContactUs/AdminContactUs/AdminMessageSubjectsPage'));
const CartPage     = lazy(() => import('./pages/CartPage'));
const CheckoutPage = lazy(() => import('./pages/CheckoutPage'));
const TermsAdminPage = lazy(() => import('./pages/TermsAdminPage'));
const AdminRefundPolicyPage = lazy(() => import('./pages/AdminRefundPolicyPage'));


function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <SavedJobsProvider>
        <SubscriptionProvider>
        <VideoFavoritesProvider>
        <DraftProvider>
        <CartProvider>
          <Router>
        <div className="App">
          <Toaster position="top-right" />

          <Suspense fallback={<FullPageSkeleton />}>
            <FloatingUpgradeButton />
          </Suspense>
          <Suspense fallback={<FullPageSkeleton />}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/MHMV-profile/jobseeker-login" element={<JobSeekerAuth />} />
              <Route path="/MHMV-profile/employee-login" element={<EmployeeAuth />} />
              <Route path="/MHMV-profile/modal-register" element={<ModalRegister />} />
              <Route path="/MHMV-profile/jobseeker-home" element={<JobSeekerHome />} />
              <Route path="/MHMV-profile/saved-jobs" element={<SavedJobs />} />
              <Route path="/MHMV-profile/recommended-jobs" element={<RecommendedJobs />} />
              <Route path="/MHMV-profile/job-search" element={<JobSearch />} />
              <Route path="/MHMV-profile/job-posting" element={<JobPosting />} />
              <Route path="/MHMV-profile/profile" element={<Profile />} />
              <Route path="/MHMV-profile/my-applications" element={<MyApplications />} />
              <Route path="/MHMV-profile/job/:id" element={<JobDetails />} />
              <Route path="/MHMV-profile/add-profile" element={<div className="app-placeholder-page">Add Profile Page</div>} />
              <Route path="/MHMV-profile/edit-profile" element={<div className="app-placeholder-page">Edit Profile Page</div>} />
              <Route path="/MHMV-profile/mv-agri" element={<div className="app-placeholder-page">MV Agri Page</div>} />
              <Route path="/MHMV-profile/audio-courses" element={<CoursePage courseType="audio" />} />
              <Route path="/MHMV-profile/audio-courses/:courseId" element={<CoursePage courseType="audio" />} />
              <Route path="/MHMV-profile/shared-audio-course/:shareToken" element={<SharedAudioCoursePage />} />
              <Route path="/MHMV-profile/shared-video-course/:shareToken" element={<SharedVideoCoursePage />} />
              <Route path="/MHMV-profile/video-courses" element={<VideoCoursesView />} />
              <Route path="/MHMV-profile/video-course/:courseId" element={<VideoCourseDetailsPage />} />
              <Route path="/MHMV-profile/student-video-course/:courseId" element={<StudentVideoCourseDetailsPage />} />
              <Route path="/MHMV-profile/audio-courses/admin" element={<AudioCourseAdmin />} />
              <Route path="/MHMV-profile/upload-course-excel" element={<VideoCourseExcelUpload />} />
              <Route path="/MHMV-profile/edit-course-options" element={<EditCourseOptions />} />  
              <Route path="/MHMV-profile/upload-course-videos/:courseId" element={<VideoCourseVideoUpload />} />
              <Route path="/MHMV-profile/courses-list" element={<CoursesListPage />} />
              <Route path="/MHMV-profile/contact-us" element={<ContactUs />} />
              <Route path="/mobile-verify" element={<MobileVerification />} />
              <Route path="/phone-login" element={<PhoneLogin />} />
              <Route path="/mv-profile" element={<MVProfile />} />
              <Route path="/MHMV-profile/video-course-home" element={<VideoCourseHomePage />} />
              <Route path="/MHMV-profile/cart"     element={<CartPage />} />
              <Route path="/MHMV-profile/checkout"  element={<CheckoutPage />} />
              <Route path="/MHMV-profile/courses" element={<CoursesLandingPage />} />
              <Route path="/MHMV-profile/apply-creator" element={<CreatorApplicationPage />} />
              <Route path="/MHMV-profile/creator-hub" element={<CreatorHubPage />} />
              <Route path="/MHMV-profile/manage-courses" element={<ManageCoursesPage />} />
              <Route path="/MHMV-profile/archived-courses" element={<ArchivedCoursesView />} />
              <Route path="/MHMV-profile/draft-video-courses" element={<DraftVideoCoursesView />} />
              <Route path="/MHMV-profile/search-results" element={<SearchResultsPage />} />
              <Route path="/MHMV-profile/upload-courses" element={<ExcelUploadComponent />} /> 
              <Route path="/MHMV-profile/video-search-results" element={<VideoSearchResultsPage />} />  
              <Route path="/MHMV-profile/common-navbar" element={<CommonNavbar />} /> 
              

  <Route path="/MHMV-profile/company-dashboard" element={<CompanyDashboard />} />
  <Route path="/MHMV-profile/company-profile" element={<CompanyProfile />} />

  <Route path="/MHMV-profile/company-team" element={<div className="app-placeholder-page">Team Management Page</div>} />
  <Route path="/MHMV-profile/company-billing" element={<SubscriptionPlans />} />
  <Route path="/MHMV-profile/subscription" element={<SubscriptionPlans />} />
  <Route path="/MHMV-profile/my-subscriptions" element={<MySubscriptions />} />
  <Route path="/MHMV-profile/my-course-purchases" element={<MyCoursePurchases />} />
  <Route path="/MHMV-profile/purchase-history" element={<MyCoursePurchases />} />
  <Route path="/MHMV-profile/checkout/:planId" element={<Checkout />} />
  <Route path="/MHMV-profile/subscription-success" element={<SubscriptionSuccess />} />

  <Route path="/MHMV-profile/applications" element={<MyApplications />} />   
  <Route path="/MHMV-profile/company-navbar" element={<CompanyNavbar />} />

  <Route path="/MHMV-profile/company-settings" element={<CompanySettings />} />
  <Route path="/MHMV-profile/company-settings/hibernate" element={<CompanySettings />} />
  <Route path="/MHMV-profile/company-settings/close-account" element={<CompanySettings />} />
  <Route path="/MHMV-profile/company-onboarding" element={<CompanyOnboardingGuard />} />
  <Route path="/MHMV-profile/my-learning" element={<LearningCoursesList />} />
  <Route path="/MHMV-profile/audio-courses/my-learning" element={<LearningCoursesList />} />
  <Route path="/MHMV-profile/video-my-learning" element={<VideoLearningPage />} />
  <Route path="/MHMV-profile/manage-jobs" element={<ManageJobsPage />} />
  <Route path="/MHMV-profile/manage-jobs/edit/:jobId" element={<EditJobPage />} />
  <Route path="/MHMV-profile/manage-jobs/view/:jobId" element={<JobDetailsPage />} />
  <Route path="/MHMV-profile/company-applications" element={<CompanyApplications />} />

  <Route path="/MHMV-profile/resdex" element={<ResdexPage />} />
  <Route path="/MHMV-profile/resdex/results" element={<ResdexResultsPage />} />
  <Route path="/MHMV-profile/draft-applications" element={<DraftApplicationsPage />} />

  {/* Archived Courses — Audio */}
  <Route path="/MHMV-profile/manage-jobs/archived-audio-courses" element={<ArchivedAudioCoursesPage />} />
  <Route path="/MHMV-profile/manage-jobs/archived-audio-course/:courseId" element={<ArchivedAudioCourseDetailPage />} />

  {/* Archived Courses — Video */}
  <Route path="/MHMV-profile/manage-jobs/archived-video-courses" element={<ArchivedVideoCoursesPage />} />
  <Route path="/MHMV-profile/manage-jobs/archived-video-course/:courseId" element={<ArchivedVideoCourseDetailPage />} />

  {/* Candidate Search — two-page recruiter flow */}
  <Route path="/MHMV-profile/candidate-search" element={<CandidateSearchPage />} />
  <Route path="/MHMV-profile/candidate-search/applicants/:jobPostingId" element={<CandidateApplicantsPage />} />

  {/* Admin — Contact Us */}
  <Route path="/MHMV-profile/admin/contact-us"                        element={<AdminContactDetailsPage />} />
  <Route path="/MHMV-profile/admin/contact-us/messages"               element={<AdminMessagesPage />} />
  <Route path="/MHMV-profile/admin/contact-us/message/:messageId"     element={<AdminMessageDetailPage />} />
  <Route path="/MHMV-profile/admin/contact-us/subjects"               element={<AdminMessageSubjectsPage />} />
  <Route path="/MHMV-profile/admin/contact-us/faqs"                   element={<AdminFaqsPage />} />
  <Route path="/MHMV-profile/admin/terms"                             element={<TermsAdminPage />} />
  <Route path="/MHMV-profile/admin/refund-policy"                     element={<AdminRefundPolicyPage />} />

            </Routes>
          </Suspense>
        </div>
          </Router>
        </CartProvider>
        </DraftProvider>
        </VideoFavoritesProvider>
        </SubscriptionProvider>
        </SavedJobsProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}

export default App;